<template>
  <modal
    ref="modal"
    size="xl"
    :show="show"
    :hide-footer="true"
    :show-close-icon="false"
    :minimization-config="minimizationConfig"
    :modal-data="modalData"
    :scrollable="false"
    modal-class="initiative-modal"
    enforce-focus-on=".cke_dialog_ui_input_text, .copy-to-clipboard-textarea"
    @hidden="onHidden"
    @hide="$emit('hide', false)"
  >
    <div class="d-flex align-items-center" slot="minimizedView">
      <i class="initiative-icon flex-shrink-0"></i>
      <span class="ml-2 text-truncate" v-no-capture>{{ currentInitiative.title }}</span>
    </div>
    <div class="content">
      <initiative-content
        :initiative="currentInitiative"
        @delete-initiatives="$refs.modal.hide()"
        :focus-add-comment-on-load="focusAddCommentOnLoad"
        :edit-initiative-on-load="editInitiativeOnLoad"
        :query="{ event: this.focusEventIdOnLoad }"
      />
    </div>
  </modal>
</template>

<script>
import { trackEvent } from '@/analytics'
import InitiativeContent from '@/components/initiative/InitiativeContent'
import Modal from '@/components/Modal'
import { EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import { ENTITY_TYPE } from '@/constants/entities'
import { addToHistory } from '@/history'
import resolveRoute from '@/router/resolveRoute'
import * as keymapService from '@/services/keymapService/keymapService'
import { getInitiativePageRouteConfig } from '@/utils/navigationConfigs'
import * as R from 'ramda'

/**
 * This component shows initiative in a Modal view
 * It shows below informations
 * - InitiativeRow in expanded view
 * - Initiative comments
 * - Ability to add a comment
 *
 * Props:
 * - show
 * - initiative
 */
export default {
  name: 'InitiativeModal',
  props: {
    show: { type: Boolean },
    initiative: { type: Object },
    focusAddCommentOnLoad: { type: Boolean },
    editInitiativeOnLoad: { type: Boolean },
    initiatives: { type: Array },
    modalData: { type: Object },
    minimizable: { type: Boolean, default: true },
    focusEventIdOnLoad: { type: Number }
  },
  components: {
    Modal,
    InitiativeContent
  },
  model: {
    prop: 'show'
  },
  data() {
    return {
      currentInitiative: this.initiative,
      minimizationConfig: this.minimizable
        ? {
            type: ENTITY_TYPE.INITIATIVE
          }
        : null
    }
  },
  computed: {
    kr() {
      return this.$store.getters.keyResultById(this.initiative.keyResultId)
    }
  },
  methods: {
    onNext() {
      const currentIndex = this.initiatives.findIndex(R.propEq('id', this.currentInitiative.id))
      if (currentIndex > -1 && currentIndex < this.initiatives.length - 1) {
        this.currentInitiative = this.initiatives[currentIndex + 1]
        this.trackNavigationEvent()
      }
    },
    onPrevious() {
      const currentIndex = this.initiatives.findIndex(R.propEq('id', this.currentInitiative.id))
      if (currentIndex > 0) {
        this.currentInitiative = this.initiatives[currentIndex - 1]
        this.trackNavigationEvent()
      }
    },
    trackNavigationEvent() {
      trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.NEXT_PREVIOUS_INITIATIVE)
    },
    saveVisit() {
      const route = getInitiativePageRouteConfig({
        initiativeId: this.initiative.id,
        orgId: this.initiative.organizationId
      })
      const initiativeRoute = resolveRoute(route).route
      const values = [
        [
          'initiativeId',
          this.initiative.id,
          {
            initiativeTitle: this.initiative.title,
            isClosed: this.initiative.isClosed,
            isDoneAndClosed: this.initiative.isDoneAndClosed,
            krTitle: this.kr.title
          }
        ]
      ]
      addToHistory({ ...initiativeRoute, values })
    },
    onHidden() {
      this.$emit('hidden', false)
      this.$emit('input', false)
    }
  },
  mounted() {
    if (this.initiatives) {
      keymapService.activateCategory('initiativeModal', {
        'previous-initiative': {
          alwaysExecute: true,
          callback: this.onPrevious
        },
        'next-initiative': {
          alwaysExecute: true,
          callback: this.onNext
        }
      })
    }
    this.kr && this.saveVisit()
  },
  beforeDestroy() {
    this.initiatives && keymapService.deactivateCategory('initiativeModal')
  },
  watch: {
    kr(current, previous) {
      current && !previous && this.saveVisit()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';

.content {
  min-height: 400px;
  margin-top: 18px;
}
</style>
<style lang="scss">
@import '~@/styles/_styleConfigurations.scss';

$sideGutter: 110px;

.initiative-modal .modal-top-actions {
  right: 10px;
  top: 14px;
}

.initiative-modal .modal-header {
  padding: 0;
}

@include media-breakpoint-down(md) {
  .initiative-modal .initiative-row {
    border-top: none !important;

    .note-section {
      margin: 0 20px 0 0;
    }

    .summary,
    > .expanded-section {
      margin-left: 0 !important;
    }
  }
}

@include media-breakpoint-up(xl) {
  .initiative-modal .modal-body {
    padding: 10px $sideGutter 10px $sideGutter;
  }
}
</style>
